import React from 'react';

// =============================================================================
// RowList
// Shared "list of action rows" used by the Imports, Budget Setup and Settings
// pages so they stay visually consistent: a labelled section containing a
// bordered card of rows, each row being  icon · title/description · actions.
// =============================================================================

// Primary (dark, filled) action button.
export const PrimaryButton = ({ children, onClick, ...rest }) => (
  <button
    onClick={onClick}
    className="px-4 py-2 text-sm font-medium text-white bg-slate-900 border border-slate-900 rounded-md transition-colors hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-1"
    {...rest}
  >
    {children}
  </button>
);

// Secondary (light, outlined) action button.
export const SecondaryButton = ({ children, onClick, ...rest }) => (
  <button
    onClick={onClick}
    className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-md transition-colors hover:bg-slate-50 dark:hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-200"
    {...rest}
  >
    {children}
  </button>
);

// A labelled section wrapping a bordered card of divided rows.
export const ListSection = ({ title, children }) => (
  <section className="mb-8 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-800/40 p-4">
    <h2 className="px-1 pb-3 text-xs font-bold tracking-wider text-slate-500 dark:text-slate-400 uppercase">
      {title}
    </h2>
    <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden divide-y divide-slate-100 dark:divide-slate-700">
      {children}
    </div>
  </section>
);

// An "N waiting" marker for a row that needs attention — a red pill beside the
// title, and the row's icon tinted to match so it is visible without reading.
// Rendered only when `alert` is truthy; nothing changes for the other rows.
export const AlertBadge = ({ children }) => (
  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold
    bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-400">
    <span aria-hidden="true">❗</span>
    {children}
  </span>
);

// A single row: actions · icon · title/description.
// Actions sit on the LEFT (close to the sidebar), then the icon, then the text
// — so buttons aren't stranded at the far right edge of a wide screen.
//
// `alert` is optional: pass a node (usually <AlertBadge>) to flag a row that has
// outstanding work behind it.
export const ListRow = ({ icon: Icon, title, description, actions, alert = null }) => (
  <div className="flex items-center gap-4 px-5 py-4 hover:bg-slate-50 dark:hover:bg-slate-700/40 transition-colors">
    {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}

    {Icon && (
      <div className={`shrink-0 w-11 h-11 rounded-lg flex items-center justify-center ${
        alert
          ? 'bg-red-100 dark:bg-red-500/15 text-red-600 dark:text-red-400'
          : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300'
      }`}>
        <Icon className="w-5 h-5" strokeWidth={1.75} />
      </div>
    )}

    <div className="flex-1 min-w-0">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-slate-100">
        {title}
        {alert}
      </h3>
      <p className="m-0 text-sm leading-snug text-slate-500 dark:text-slate-400">{description}</p>
    </div>
  </div>
);

export default { ListSection, ListRow, PrimaryButton, SecondaryButton, AlertBadge };
